import type { PickingInfo } from "@deck.gl/core";
import { WebMercatorViewport } from "@deck.gl/core";
import { PathStyleExtension } from "@deck.gl/extensions";
import { GeoJsonLayer } from "@deck.gl/layers";
import { useQuery } from "@tanstack/react-query";
import { bbox } from "@turf/bbox";
import { bboxPolygon } from "@turf/bbox-polygon";
import { buffer } from "@turf/buffer";
import { featureCollection, point as turfPoint } from "@turf/helpers";
import type { BBox, Geometry, Point } from "geojson";
import { useEffect, useMemo, useRef, useState } from "react";
import type { Site } from "../../apiClient/generated";
import { useMapApiClient } from "../../hooks";
import {
    centerOfPoints,
    createFeatureCollection,
} from "../../utils/geopatialUtils";
import {
    EMISSION_LAYER_STYLES,
    MAP_ZOOM_SHOW_DETAILS,
} from "../MapV2/constants";
import { PlumeOpacityPanel } from "../MapV2/elements/DataControls/PlumeControl";
import { useMap } from "../MapV2/hooks/mapState";
import {
    EmissionRecordsLayers,
    PlumeOutlinesLayer,
} from "../MapV2/layers/emissions";
import { SiteLayer } from "../MapV2/layers/infrastructure";
import { RasterLayer } from "../MapV2/layers/rasters";
import { MapBase } from "../MapV2/Map";
import { MapPopup, type PickedItems } from "../MapV2/MapPopup";
import { useNearbySites } from "./nearbySites";

// Area the map frames and loads context for: the detections, padded by 1km.
const contextArea = (points: Point[]): BBox | undefined => {
    const features = points
        .filter((point) => point?.coordinates)
        .map((point) => turfPoint(point.coordinates));
    if (!features.length) {
        return undefined;
    }
    const buffered = buffer(featureCollection(features), 1, {
        units: "kilometers",
    });
    return buffered ? bbox(buffered) : undefined;
};

// Outlined rather than filled, to keep the plume underneath readable. White
// under black dashes reads as a dashed border on any basemap.
const selectedSiteHighlight = (site?: Site | null) => {
    let geometry = site?.shape as Geometry | undefined;
    if (!geometry && site?.location?.coordinates) {
        geometry = buffer(
            turfPoint(site.location.coordinates as [number, number]),
            60,
            { units: "meters" },
        )?.geometry;
    }
    if (!geometry) {
        return [];
    }

    const shared = {
        data: {
            type: "FeatureCollection" as const,
            features: [{ type: "Feature" as const, properties: {}, geometry }],
        },
        filled: false,
        stroked: true,
        pickable: false,
        lineWidthUnits: "pixels" as const,
        getLineWidth: 3,
    };

    return [
        new GeoJsonLayer({
            ...shared,
            id: "unmatched-selected-site-outline",
            getLineColor: [255, 255, 255, 255],
        }),
        new GeoJsonLayer({
            ...shared,
            id: "unmatched-selected-site-dashes",
            getLineColor: [0, 0, 0, 255],
            getDashArray: [8, 8],
            dashJustified: true,
            extensions: [new PathStyleExtension({ dash: true })],
        }),
    ];
};

// Picking a site off the map: a click landing on exactly one selects it, while
// a click landing on several (or on a cluster) opens the popup to disambiguate.
const siteClickHandler =
    (
        onSelectSite: (siteId: string) => void,
        setPickedSites: (picked: PickedItems | undefined) => void,
    ) =>
    ({ info }: { info: PickingInfo[] }) => {
        const sites = info.filter(
            (i) =>
                i.layer.id.includes("infrastructure-sites-points") ||
                i.layer.id.includes("infrastructure-sites-shapes"),
        );
        const siteClusters = info.filter((i) =>
            i.layer.id.includes("infrastructure-sites-clusters"),
        );

        if (sites.length === 0 && siteClusters.length === 0) {
            setPickedSites(undefined);
            return;
        }

        if (sites.length === 1 && siteClusters.length === 0) {
            setPickedSites(undefined);
            onSelectSite(sites[0].object.properties.id);
            return;
        }

        // MapPopup expands clusters into their member sites, so they need no
        // separate handling.
        setPickedSites({
            // Canvas-relative: the popup is positioned absolutely inside the
            // map box.
            pointClicked: { x: info[0].x, y: info[0].y },
            sites,
            siteClusters,
            equipment: [],
            pipelines: [],
            emissions: [],
            emissionClusters: [],
            dataPoints: [],
            unmatchedDataPoints: [],
            extraData: [],
        });
    };

// Accepts both UnmatchedDataPoint and DataPointMap.
interface MinimapDataPoint {
    id: string;
    location: { type?: string; coordinates?: number[] };
    plumeImage?: string | null;
}

interface DecisionMinimapProps {
    dataPoints: MinimapDataPoint[];
    mapId: string;
    selectedSite?: Site | null;
    // Omit to keep the map read-only.
    onSelectSite?: (siteId: string) => void;
    className?: string;
}

export const DecisionMinimap = (props: DecisionMinimapProps) => {
    const { flyTo, debounced } = useMap(props.mapId);
    const mapApiClient = useMapApiClient();

    const containerRef = useRef<HTMLDivElement>(null);
    const [pickedSites, setPickedSites] = useState<PickedItems | undefined>();
    const [showPlumes, setShowPlumes] = useState(true);
    const [plumeOpacity, setPlumeOpacity] = useState(0.5);
    const [oldPlumeOpacity, setOldPlumeOpacity] = useState(0);

    const locations = useMemo(
        () => props.dataPoints.map((dataPoint) => dataPoint.location as Point),
        [props.dataPoints],
    );
    const area = useMemo(() => contextArea(locations), [locations]);

    // Deferred so deck.gl has sized the canvas before the camera is moved.
    useEffect(() => {
        if (!area) {
            return;
        }
        const timeout = setTimeout(() => {
            const container = containerRef.current;
            if (!container) {
                return;
            }
            const [minLng, minLat, maxLng, maxLat] = area;
            const { latitude, longitude, zoom } = new WebMercatorViewport({
                width: container.clientWidth,
                height: container.clientHeight,
            }).fitBounds(
                [
                    [minLng, minLat],
                    [maxLng, maxLat],
                ],
                { padding: 32 },
            );
            flyTo(latitude, longitude, zoom, true);
        }, 300);
        return () => clearTimeout(timeout);
    }, [area, flyTo]);

    // Same sites the site picker offers.
    const nearbySitesQuery = useNearbySites(centerOfPoints(locations));

    // The filter takes a GeoJSON geometry, not a bounding box.
    const areaGeometry = useMemo(
        () => (area ? bboxPolygon(area).geometry : undefined),
        [area],
    );

    const plumeDataPointIds = useMemo(
        () =>
            props.dataPoints
                .filter((dataPoint) => dataPoint.plumeImage)
                .map((dataPoint) => dataPoint.id),
        [props.dataPoints],
    );

    const plumesQuery = useQuery({
        queryKey: ["unmatchedPlumeImages", plumeDataPointIds],
        enabled: plumeDataPointIds.length > 0,
        queryFn: async () => {
            const response = await mapApiClient.mapUnmatchedDataPlumesList({
                dataPoints: plumeDataPointIds,
            });
            return response.results;
        },
    });

    // Fetched for the whole area at once, rather than per data point.
    const outlinesQuery = useQuery({
        queryKey: ["unmatchedPlumeOutlines", areaGeometry],
        enabled: !!areaGeometry,
        queryFn: async () => {
            const response =
                await mapApiClient.mapUnmatchedDataPlumeOutlinesList({
                    locationWithin: JSON.stringify(areaGeometry),
                });
            return response.results;
        },
    });

    const layers = useMemo(
        () => [
            ...SiteLayer(
                createFeatureCollection(nearbySitesQuery.data || []).features,
                true,
                // SiteLayer draws nothing below zoom 7.
                Math.max(debounced.viewState?.zoom ?? 0, 7),
            ),
            PlumeOutlinesLayer(
                // Plumes with imagery of their own are drawn as rasters.
                (outlinesQuery.data || []).filter(
                    (p) => !showPlumes || !p.hasPlume,
                ),
                "unmatched_plume_outlines",
                true,
                MAP_ZOOM_SHOW_DETAILS + 1,
                plumeOpacity,
            ),
            RasterLayer({
                data: plumesQuery.data || [],
                name: "unmatched_plume_images",
                visible: showPlumes,
                zoomLevel: MAP_ZOOM_SHOW_DETAILS + 1,
                opacity: plumeOpacity,
                detail: true,
            }),
            EmissionRecordsLayers(
                createFeatureCollection(props.dataPoints).features,
                true,
                MAP_ZOOM_SHOW_DETAILS + 1,
                "unmatched_detections",
                EMISSION_LAYER_STYLES.operatorProvided,
            ),
            ...selectedSiteHighlight(props.selectedSite),
        ],
        [
            nearbySitesQuery.data,
            debounced.viewState?.zoom,
            outlinesQuery.data,
            plumesQuery.data,
            showPlumes,
            plumeOpacity,
            props.dataPoints,
            props.selectedSite,
        ],
    );

    return (
        <div
            ref={containerRef}
            className={`relative overflow-hidden rounded border border-ae-gray-250 ${
                props.className ?? "h-[400px]"
            }`}
        >
            <MapBase
                mapId={props.mapId}
                layers={[layers]}
                cursor={props.onSelectSite ? "pointer" : undefined}
                onLeftClick={
                    props.onSelectSite
                        ? siteClickHandler(props.onSelectSite, setPickedSites)
                        : undefined
                }
                getTooltip={(info) => {
                    const properties = info.object?.properties;
                    if (!properties) {
                        return null;
                    }
                    if ("siteName" in properties) {
                        return properties.siteName;
                    }
                    if ("detectedRate" in properties) {
                        return "Unmatched detection";
                    }
                    return null;
                }}
                showScaleControl
                showZoomControl
                showBasemapControl
            />

            {!!pickedSites && (
                <MapPopup
                    picked={pickedSites}
                    onClickItem={(item) => {
                        setPickedSites(undefined);
                        const siteId = item.object?.properties?.id;
                        if (siteId) {
                            props.onSelectSite?.(siteId);
                        }
                    }}
                />
            )}

            {plumeDataPointIds.length > 0 && (
                <div className="absolute top-2 w-full flex justify-center">
                    <PlumeOpacityPanel
                        className="w-4/5 overflow-x-auto"
                        plumeOpacity={plumeOpacity}
                        showPlumes={showPlumes}
                        onOpacityChange={setPlumeOpacity}
                        onToggleVisibility={() => {
                            if (plumeOpacity === 0) {
                                setPlumeOpacity(oldPlumeOpacity || 0.5);
                                setOldPlumeOpacity(0);
                            } else {
                                setOldPlumeOpacity(plumeOpacity);
                                setPlumeOpacity(0);
                            }
                        }}
                        onShowPlumesChange={() => setShowPlumes(!showPlumes)}
                    />
                </div>
            )}
        </div>
    );
};
